import React, { useEffect, useMemo, useState } from "react"

import {
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Autocomplete, AutocompleteItem } from "@heroui/react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { TraceFilterConfig } from "@/types/metrics/metadata"

import {
  useCustomTraceMetricPreviewTransport,
  useTraceFilterSuggestionsTransport,
} from "../routes/useMetricConfigurationTransport"
import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { TraceFilterSection } from "../trace-filter"
import {
  AGGREGATION_METHOD_OPTIONS,
  CUSTOM_TRACE_UNIT_OPTIONS,
  FALLBACK_ATTRIBUTES,
  isAggregationMethod,
  KNOWN_ATTRIBUTES_BY_SPAN,
  KNOWN_SPAN_NAMES,
  requiresNumericAttribute,
  resolveCustomTraceUnit,
  type AggregationMethod,
  type CustomTraceUnitOptionKey,
} from "../utils/traceConstants"
import {
  MAX_TRACE_AGENT_SCOPE_IDS,
  toTraceAgentIds,
} from "./customTraceAgentScope"
import { CustomTracePreview } from "./CustomTracePreview"
import { RequiredMarker } from "./RequiredMarker"

interface CustomTraceConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  selectedAgentIds?: string[]
}

interface SuggestionOption {
  key: string
  label: string
  description: string
}

/**
 * Max length of the free-text custom unit, kept in sync with the `unit`
 * field's `.max(32)` constraint in the metrics router schema.
 */
const MAX_CUSTOM_UNIT_LENGTH = 32

type MetricMetadata = MetricRecordData["metric_metadata"]

/**
 * Merges live attribute/span keys observed in recent traces with a curated
 * fallback list. Live keys take precedence and are de-duplicated (trimmed,
 * case-sensitive); fallback options only appear if not already present.
 */
function mergeSuggestionOptions(
  liveKeys: string[] | undefined,
  fallbackOptions: SuggestionOption[],
): SuggestionOption[] {
  const seen = new Set<string>()
  const options: SuggestionOption[] = []

  for (const key of liveKeys ?? []) {
    const trimmed = key.trim()
    if (!trimmed || seen.has(trimmed)) {
      continue
    }
    seen.add(trimmed)
    options.push({
      key: trimmed,
      label: trimmed,
      description: "Seen in recent traces",
    })
  }

  for (const option of fallbackOptions) {
    if (seen.has(option.key)) {
      continue
    }
    seen.add(option.key)
    options.push(option)
  }

  return options
}

function getImpliedUnit(
  method: AggregationMethod,
): CustomTraceUnitOptionKey | undefined {
  if (method === "count") {
    return "count"
  }
  if (method === "error_rate" || method === "success_rate") {
    return "percent"
  }
  return undefined
}

function inferUnitKey(
  metadataUnit: string | undefined,
  hasExplicitUnit: boolean,
): CustomTraceUnitOptionKey {
  if (metadataUnit === "" && hasExplicitUnit) {
    return "custom"
  }
  if (!metadataUnit) {
    return "none"
  }
  const predefined = CUSTOM_TRACE_UNIT_OPTIONS.find(
    (option) => option.key === metadataUnit,
  )
  return predefined ? predefined.key : "custom"
}

function withoutUnitMetadata(metadata: MetricMetadata): MetricMetadata {
  const {
    unit: _unit,
    units: _units,
    unit_abbreviation: _unitAbbreviation,
    ...metadataWithoutUnit
  } = metadata
  return metadataWithoutUnit
}

function withoutMetricAttribute(metadata: MetricMetadata): MetricMetadata {
  const { metric_attribute: _metricAttribute, ...metadataWithoutAttribute } =
    metadata
  return metadataWithoutAttribute
}

function isRateAggregationMethod(method: AggregationMethod): boolean {
  return method === "error_rate" || method === "success_rate"
}

export const CustomTraceConfig: React.FC<CustomTraceConfigProps> = ({
  metricRecordData,
  onChange,
  selectedAgentIds = [],
}) => {
  const metricMetadata = useMemo(
    () => metricRecordData.metric_metadata ?? {},
    [metricRecordData.metric_metadata],
  )
  const selectedAgentScopeIds = useMemo(
    () => Array.from(new Set(selectedAgentIds)).sort(),
    [selectedAgentIds],
  )
  const traceAgentIds = useMemo(
    () => toTraceAgentIds(selectedAgentScopeIds),
    [selectedAgentScopeIds],
  )
  const hasTooManyTraceAgentIds =
    selectedAgentScopeIds.length > MAX_TRACE_AGENT_SCOPE_IDS
  const traceScopeInput = useMemo(
    () => (traceAgentIds ? { agent_ids: traceAgentIds } : undefined),
    [traceAgentIds],
  )
  const isAgentScoped = selectedAgentScopeIds.length > 0
  const currentSpanName = metricMetadata.span_name || ""
  const normalizedSpanName = currentSpanName.trim()
  const currentMetricAttribute = metricMetadata.metric_attribute ?? ""
  const rawAggregationMethod = metricMetadata.aggregation_method ?? ""
  const currentAggregationMethod: AggregationMethod = isAggregationMethod(
    rawAggregationMethod,
  )
    ? rawAggregationMethod
    : "average"
  const hasInvalidAggregationMethod =
    rawAggregationMethod.length > 0 &&
    !isAggregationMethod(rawAggregationMethod)
  const currentValueSource =
    metricMetadata.value_source === "duration" ? "duration" : "attribute"
  const isDuration = currentValueSource === "duration"
  const needsNumericAttribute = requiresNumericAttribute(
    currentAggregationMethod,
  )
  const hasMetricAttribute = currentMetricAttribute.trim().length > 0

  const spanSuggestionsQuery = useTraceFilterSuggestionsTransport({
    request: traceScopeInput ?? {},
    enabled: !hasTooManyTraceAgentIds,
  })
  const attributeSuggestionsQuery = useTraceFilterSuggestionsTransport({
    request: {
      ...(traceScopeInput ?? {}),
      span_name: normalizedSpanName || undefined,
    },
    enabled: normalizedSpanName.length > 0 && !hasTooManyTraceAgentIds,
  })
  const previewQuery = useCustomTraceMetricPreviewTransport({
    request: {
      span_name: currentSpanName,
      ...(traceScopeInput ?? {}),
      ...(isDuration
        ? {
            metric_attribute: currentMetricAttribute,
            value_source: currentValueSource,
          }
        : hasMetricAttribute
          ? { metric_attribute: currentMetricAttribute }
          : {}),
    },
    enabled: normalizedSpanName.length > 0 && !hasTooManyTraceAgentIds,
  })

  const spanSuggestions = useMemo(
    () =>
      mergeSuggestionOptions(
        spanSuggestionsQuery.data?.span_names,
        isAgentScoped ? [] : KNOWN_SPAN_NAMES,
      ),
    [isAgentScoped, spanSuggestionsQuery.data?.span_names],
  )

  const mergedAttributeSuggestions = useMemo(() => {
    const fallbackAttributeSuggestions = isAgentScoped
      ? []
      : (KNOWN_ATTRIBUTES_BY_SPAN[normalizedSpanName] ?? FALLBACK_ATTRIBUTES)
    return mergeSuggestionOptions(
      attributeSuggestionsQuery.data?.attribute_keys,
      fallbackAttributeSuggestions,
    )
  }, [
    attributeSuggestionsQuery.data?.attribute_keys,
    isAgentScoped,
    normalizedSpanName,
  ])

  const metadataUnit = resolveCustomTraceUnit(metricMetadata)
  const hasExplicitUnit = Object.hasOwn(metricMetadata, "unit")
  const impliedUnit = getImpliedUnit(currentAggregationMethod)
  const [selectedUnitKey, setSelectedUnitKey] =
    useState<CustomTraceUnitOptionKey>(() =>
      inferUnitKey(metadataUnit, hasExplicitUnit),
    )
  const lockedUnitKey: CustomTraceUnitOptionKey | undefined = isDuration
    ? "s"
    : impliedUnit
  const currentUnitKey = lockedUnitKey ?? selectedUnitKey
  const isUnitLocked = Boolean(lockedUnitKey)
  const customUnitValue =
    currentUnitKey === "custom" && metadataUnit ? metadataUnit : ""
  const preview = previewQuery.data
  const previewIsPending = previewQuery.isLoading || previewQuery.isFetching
  const hasResolvedPreview =
    Boolean(preview) && !previewIsPending && !previewQuery.isError
  const selectedAttributeIsNumeric =
    hasMetricAttribute &&
    hasResolvedPreview &&
    Boolean(preview?.isNumeric) &&
    (preview?.matchedSpanCount ?? 0) > 0
  const selectedAttributeIsBoolean =
    hasMetricAttribute &&
    hasResolvedPreview &&
    Boolean(preview?.isBoolean) &&
    (preview?.matchedSpanCount ?? 0) > 0
  const hasSpanStatusRateSupport =
    !isDuration &&
    !hasMetricAttribute &&
    hasResolvedPreview &&
    (preview?.okStatusCount ?? 0) + (preview?.errorStatusCount ?? 0) > 0
  const hasRateSupport = selectedAttributeIsBoolean || hasSpanStatusRateSupport
  const selectedValueIsNumeric = isDuration || selectedAttributeIsNumeric
  const aggregationOptions = useMemo(
    () =>
      AGGREGATION_METHOD_OPTIONS.filter(({ key }) => {
        if (requiresNumericAttribute(key)) {
          return selectedValueIsNumeric
        }
        if (key === "count") {
          return true
        }
        return !isDuration && hasRateSupport
      }),
    [hasRateSupport, isDuration, selectedValueIsNumeric],
  )
  const displayedAggregationMethod = aggregationOptions.some(
    ({ key }) => key === currentAggregationMethod,
  )
    ? currentAggregationMethod
    : "count"
  const shouldShowAggregationMethod = aggregationOptions.length > 1
  const shouldShowUnitField =
    requiresNumericAttribute(displayedAggregationMethod) &&
    selectedValueIsNumeric
  const previewRequiresNumericAttribute =
    needsNumericAttribute && shouldShowAggregationMethod

  const updateMetadata = (metadata: MetricRecordData["metric_metadata"]) => {
    const aggregationMethod = metadata.aggregation_method ?? ""
    const normalizedMetadata =
      hasInvalidAggregationMethod && !isAggregationMethod(aggregationMethod)
        ? {
            ...metadata,
            aggregation_method: currentAggregationMethod,
          }
        : metadata
    const metadataForUpdate =
      normalizedMetadata.metric_attribute?.trim() ||
      normalizedMetadata.value_source === "duration"
        ? normalizedMetadata
        : withoutMetricAttribute(normalizedMetadata)

    onChange({
      ...metricRecordData,
      metric_metadata: metadataForUpdate,
    })
  }

  useEffect(() => {
    if (!normalizedSpanName || !hasResolvedPreview) {
      return
    }

    const numericAggregationWithoutNumericAttribute =
      requiresNumericAttribute(currentAggregationMethod) &&
      !selectedValueIsNumeric
    const rateAggregationWithoutStatusData =
      isRateAggregationMethod(currentAggregationMethod) && !hasRateSupport
    const shouldForceCount =
      numericAggregationWithoutNumericAttribute ||
      rateAggregationWithoutStatusData
    const shouldClearUnitMetadata =
      shouldForceCount || !requiresNumericAttribute(currentAggregationMethod)
    const hasUnitMetadata =
      Object.hasOwn(metricMetadata, "unit") ||
      Object.hasOwn(metricMetadata, "units") ||
      Object.hasOwn(metricMetadata, "unit_abbreviation")
    const hasEmptyAttributeMetadata =
      Object.hasOwn(metricMetadata, "metric_attribute") && !hasMetricAttribute

    if (
      !shouldForceCount &&
      !(shouldClearUnitMetadata && hasUnitMetadata) &&
      !hasEmptyAttributeMetadata
    ) {
      return
    }

    let nextMetadata: MetricMetadata = {
      ...metricMetadata,
      ...(shouldForceCount ? { aggregation_method: "count" } : {}),
    }
    if (shouldClearUnitMetadata) {
      nextMetadata = withoutUnitMetadata(nextMetadata)
    }
    if (!hasMetricAttribute) {
      nextMetadata = withoutMetricAttribute(nextMetadata)
    }

    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }, [
    currentAggregationMethod,
    hasMetricAttribute,
    hasRateSupport,
    hasResolvedPreview,
    metricMetadata,
    metricRecordData,
    normalizedSpanName,
    onChange,
    selectedAttributeIsBoolean,
    selectedAttributeIsNumeric,
    selectedValueIsNumeric,
  ])

  const handleSpanNameChange = (value: string) => {
    updateMetadata({
      ...metricMetadata,
      span_name: value,
    })
  }

  const handleTraceFilterChange = (
    nextValue: TraceFilterConfig | undefined,
  ) => {
    if (nextValue === undefined) {
      const { trace_filter: _ignored, ...rest } = metricMetadata
      updateMetadata(rest)
      return
    }
    updateMetadata({
      ...metricMetadata,
      trace_filter: nextValue,
    })
  }

  const handleMetricAttributeChange = (value: string) => {
    updateMetadata({
      ...metricMetadata,
      metric_attribute: value,
    })
  }

  const handleAggregationMethodChange = (method: AggregationMethod) => {
    const nextImpliedUnit = getImpliedUnit(method)
    const nextMetadata = {
      ...metricMetadata,
      aggregation_method: method,
    }
    const shouldClearAutoUnit =
      (impliedUnit && metadataUnit === impliedUnit) ||
      (nextImpliedUnit && metadataUnit === nextImpliedUnit)
    const shouldClearUnitMetadata =
      shouldClearAutoUnit || !requiresNumericAttribute(method)
    const metadataForUpdate = shouldClearUnitMetadata
      ? withoutUnitMetadata(nextMetadata)
      : nextMetadata

    if (!nextImpliedUnit) {
      setSelectedUnitKey(
        inferUnitKey(
          resolveCustomTraceUnit(metadataForUpdate),
          Object.hasOwn(metadataForUpdate, "unit"),
        ),
      )
    }
    updateMetadata(metadataForUpdate)
  }

  const handleValueSourceChange = (value: string) => {
    if (value === "duration") {
      setSelectedUnitKey("s")
      updateMetadata({
        ...metricMetadata,
        value_source: "duration",
        metric_attribute: "",
        unit: "s",
        units: "s",
        unit_abbreviation: "s",
      })
      return
    }
    const { value_source: _valueSource, ...metadataWithoutValueSource } =
      metricMetadata
    const restoredMetadata = withoutUnitMetadata(metadataWithoutValueSource)
    setSelectedUnitKey(
      inferUnitKey(
        resolveCustomTraceUnit(restoredMetadata),
        Object.hasOwn(restoredMetadata, "unit"),
      ),
    )
    updateMetadata(restoredMetadata)
  }

  const handleUnitChange = (unitKey: CustomTraceUnitOptionKey) => {
    const option = CUSTOM_TRACE_UNIT_OPTIONS.find(({ key }) => key === unitKey)
    const unit = option?.metadataUnit
    if (unitKey === "none") {
      setSelectedUnitKey("none")
      updateMetadata(withoutUnitMetadata(metricMetadata))
      return
    }
    if (unitKey === "custom") {
      setSelectedUnitKey("custom")
      updateMetadata({
        ...metricMetadata,
        unit: "",
        units: "",
        unit_abbreviation: "",
      })
      return
    }
    setSelectedUnitKey(unitKey)
    updateMetadata({
      ...metricMetadata,
      unit,
      units: unit,
      unit_abbreviation: unit,
    })
  }

  const handleCustomUnitChange = (value: string) => {
    const unit = value.slice(0, MAX_CUSTOM_UNIT_LENGTH)
    setSelectedUnitKey("custom")
    updateMetadata({
      ...metricMetadata,
      unit,
      units: unit,
      unit_abbreviation: unit,
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Span Name
            <RequiredMarker />
          </label>
        </div>
        <Autocomplete
          allowsCustomValue
          inputValue={currentSpanName}
          onInputChange={handleSpanNameChange}
          onSelectionChange={(key) => {
            if (typeof key === "string") {
              handleSpanNameChange(key)
            }
          }}
          placeholder="e.g. llm, tts, stt"
          menuTrigger="focus"
          variant="flat"
          aria-label="Span name"
        >
          {spanSuggestions.map((span) => (
            <AutocompleteItem
              key={span.key}
              textValue={span.key}
              description={span.description}
            >
              {span.label}
            </AutocompleteItem>
          ))}
        </Autocomplete>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Aggregate</label>
        </div>
        <RadioGroup
          value={currentValueSource}
          onValueChange={handleValueSourceChange}
          aria-label="Aggregate"
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value="attribute"
              id="value-source-attribute"
              aria-label="Span attribute"
            />
            <label
              htmlFor="value-source-attribute"
              className="text-sm cursor-pointer"
            >
              Span attribute
            </label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value="duration"
              id="value-source-duration"
              aria-label="Span duration"
            />
            <label
              htmlFor="value-source-duration"
              className="text-sm cursor-pointer"
            >
              Span duration
            </label>
          </div>
        </RadioGroup>
      </div>

      {!isDuration && normalizedSpanName && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label className={LABEL_STYLES}>Metric Attribute</label>
          </div>
          <Autocomplete
            allowsCustomValue
            inputValue={currentMetricAttribute}
            onInputChange={handleMetricAttributeChange}
            onSelectionChange={(key) => {
              if (typeof key === "string") {
                handleMetricAttributeChange(key)
              }
            }}
            placeholder="Optional; e.g. metrics.ttfb, stt.providerName"
            menuTrigger="focus"
            variant="flat"
            aria-label="Metric attribute"
          >
            {mergedAttributeSuggestions.map((attr) => (
              <AutocompleteItem
                key={attr.key}
                textValue={attr.key}
                description={attr.description}
              >
                {attr.label}
              </AutocompleteItem>
            ))}
          </Autocomplete>
        </div>
      )}

      {shouldShowAggregationMethod ? (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label className={LABEL_STYLES}>Aggregation Method</label>
          </div>
          <Select
            value={displayedAggregationMethod}
            onValueChange={(value) => {
              if (isAggregationMethod(value)) {
                handleAggregationMethodChange(value)
              }
            }}
          >
            <SelectTrigger aria-label="Select aggregation method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {aggregationOptions.map((method) => (
                <SelectItem key={method.key} value={method.key}>
                  {method.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : normalizedSpanName ? (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label className={LABEL_STYLES}>Aggregation Method</label>
          </div>
          <div
            className="flex h-10 items-center rounded-md bg-surface-1 px-3 text-sm text-coval-text"
            data-testid="custom-trace-implicit-aggregation"
          >
            Count
          </div>
        </div>
      ) : null}

      {shouldShowUnitField && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label className={LABEL_STYLES}>Unit</label>
          </div>
          <Select
            value={currentUnitKey}
            onValueChange={(value) =>
              handleUnitChange(value as CustomTraceUnitOptionKey)
            }
            disabled={isUnitLocked}
          >
            <SelectTrigger aria-label="Select custom trace metric unit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CUSTOM_TRACE_UNIT_OPTIONS.map((unit) => (
                <SelectItem key={unit.key} value={unit.key}>
                  {unit.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentUnitKey === "custom" && !isUnitLocked && (
            <input
              aria-label="Custom unit text"
              className="mt-2 h-10 w-full rounded-md border border-coval-border bg-surface-1 px-3 text-sm outline-none focus:border-coval-border-active"
              maxLength={MAX_CUSTOM_UNIT_LENGTH}
              value={customUnitValue}
              onChange={(event) => handleCustomUnitChange(event.target.value)}
            />
          )}
        </div>
      )}

      <CustomTracePreview
        currentSpanName={currentSpanName}
        hasMetricAttribute={hasMetricAttribute}
        hasTooManyTraceAgentIds={hasTooManyTraceAgentIds}
        isDuration={isDuration}
        preview={preview}
        previewIsError={previewQuery.isError}
        previewIsPending={previewIsPending}
        previewRequiresNumericAttribute={previewRequiresNumericAttribute}
      />

      <TraceFilterSection
        value={metricMetadata.trace_filter}
        onChange={handleTraceFilterChange}
      />
    </div>
  )
}
